<template>
  <div
    class="bg-white mb-[24px]"
  >
    <!-- Header -->
    <div class="flex items-center justify-between gap-[8px] mb-[24px] px-[20px] py-[16px] border-b-[1px] border-b-[#E5E5E5]">
      <div class="flex items-center gap-[8px] shrink-1">
        <Button
          outline
          type="icon"
          @click="$router.go(-1)"
        >
          <span class="k-arrow-left-2 text-[#0A0A0A]" />
        </Button>
        <span class="text-[#0A0A0A] font-semibold">
          Outbond Non Komship
        </span>
      </div>
      <DateRangeFilter
        v-model="dateRange"
        :max-date="today"
        :ranges="rangesConfig"
        :date-format="disableFutureDates"
        class="max-w-[300px] mt-[5px]"
      />
    </div>

    <!-- Filter Bar -->
    <div class="flex flex-col sm:flex-row items-center justify-between gap-[16px] mb-[16px] px-[24px]">
      <!-- Tabs -->
      <Tabs>
        <TabItem
          :to="{ query: { ...$route.query, status: undefined } }"
          :active="!$route.query.status"
          replace
        >
          Semua
        </TabItem>
        <TabItem
          v-for="status in statusList"
          :key="status.value"
          :to="{ query: { ...$route.query, status: status.value } }"
          :active="isActiveTab(status)"
          replace
        >
          {{ status.label }}
        </TabItem>
      </Tabs>

      <!-- Filters -->
      <div class="flex items-center justify-end gap-[12px]">
        <div class="w-full sm:max-w-[250px]">
          <UIInput
            v-model="searchedOutboundId"
            placeholder="Cari Outbond ID"
          >
            <template #suffix>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0A0A0A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                />
                <line
                  x1="21"
                  y1="21"
                  x2="16.65"
                  y2="16.65"
                />
              </svg>
            </template>
          </UIInput>
        </div>
        <div class="w-full sm:max-w-[200px]">
          <Select
            v-model="selectedPartner"
            :fetcher="fetcherPartners"
            :reduce="option => option.name"
            option-label="name"
            placeholder="Nama Partner"
            :clearable="true"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="px-[24px] pb-[24px]">
      <Table
        ref="tableNonKomship"
      />
    </div>
  </div>
</template>

<script>
import { generateLastDays, today } from '@/store/helpers'
import { YEAR_MONTH_DAY, createDatePickerRestrictor } from '@/libs/filterDate'
import {
  Button, Input as UIInput, Select, Tabs, TabItem,
} from '@/@core/components/ui'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { toastError } from '@/libs/toastification'
import moment from 'moment'
import { datePickerRangeGeneral } from '../../storage-request/config'
import DateRangeFilter from './DateRangeFilter.vue'
import { statusList } from './config'
import Table from './Table.vue'

export default {
  name: 'NonKomship',
  components: {
    Table,
    Button,
    UIInput,
    Select,
    DateRangeFilter,
    Tabs,
    TabItem,
  },
  data() {
    const startDateQuery = this.$route.query.start_date
    const endDateQuery = this.$route.query.end_date
    let initialStartDate = generateLastDays(today, 30)
    let initialEndDate = today

    if (startDateQuery && moment(startDateQuery).isValid()) {
      initialStartDate = moment(startDateQuery).toDate()
    }
    if (endDateQuery && moment(endDateQuery).isValid()) {
      initialEndDate = moment(endDateQuery).toDate()
    }

    return {
      today,
      statusList,
      searchedOutboundId: this.$route.query.outbound_id || '',
      selectedPartner: this.$route.query.partner_name ? { name: this.$route.query.partner_name } : null,
      fetcherPartners: null,
      dateRange: {
        startDate: initialStartDate,
        endDate: initialEndDate,
      },
    }
  },
  computed: {
    currentStatus() {
      return this.$route.query.status || 'all'
    },
    rangesConfig() {
      return datePickerRangeGeneral(today)
    },
  },
  watch: {
    searchedOutboundId(value) {
      this.$router.replace({
        query: { ...this.$route.query, outbound_id: value || undefined },
      }).catch(() => {})
      this.debouncedFilter()
    },
    selectedPartner(value) {
      const partnerName = value?.name ?? value ?? undefined
      const query = { ...this.$route.query, partner_name: partnerName }
      delete query.partner_id
      this.$router.replace({
        query,
      }).catch(() => {})
      this.debouncedFilter()
    },
    dateRange(value) {
      if (!value) {
        this.$router.replace({
          query: { ...this.$route.query, start_date: undefined, end_date: undefined },
        }).catch(() => {})
        this.debouncedFilter()
        return
      }

      const startDate = YEAR_MONTH_DAY(value.startDate)
      const endDate = YEAR_MONTH_DAY(value.endDate)
      if (this.$route.query.start_date === startDate && this.$route.query.end_date === endDate) return

      this.$router.replace({
        query: { ...this.$route.query, start_date: startDate, end_date: endDate },
      }).catch(() => {})
      this.debouncedFilter()
    },
    currentStatus() {
      this.debouncedFilter()
    },
  },
  created() {
    this.initFetcherPartners()
    this.debouncedFilter = _.debounce(() => {
      if (this.$refs.tableNonKomship) {
        const status = this.currentStatus === 'all' ? undefined : this.currentStatus
        const partnerName = this.selectedPartner?.name ?? this.selectedPartner ?? undefined
        this.$refs.tableNonKomship.applyFilters({
          date_range: this.dateRange,
          outbound_id: this.searchedOutboundId || undefined,
          partner_name: partnerName,
          status,
        })
      }
    }, 500)
  },
  mounted() {
    this.debouncedFilter()
  },
  beforeDestroy() {
    if (this.debouncedFilter) {
      this.debouncedFilter.cancel()
    }
    if (this.fetcherPartners && this.fetcherPartners.cancel) {
      this.fetcherPartners.cancel()
    }
  },
  methods: {
    initFetcherPartners() {
      this.fetcherPartners = createPaginatedFetcher(
        ({
          offset, limit, search, cancelToken,
        }) => this.$http_kompack
          .get('v1/kompack/outbound/non-pickup/partners', {
            params: { offset, limit, search: search || undefined },
            cancelToken,
          })
          .then(response => ({ data: response.data.data || [] }))
          .catch(() => {
            toastError('Gagal mengambil daftar partner. Silakan coba lagi.')
            return { data: [] }
          }),
        0,
        50,
      )
      this.fetcherPartners.fetchData()
    },
    disableFutureDates: createDatePickerRestrictor(),
    isActiveTab(status) {
      return this.$route.query.status === status.value
    },
  },
}
</script>
